/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useRef } from 'react';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { isLoaderError } from '@atlassian/react-loosely-lazy/lazy/errors';
import { getIsInJiraHoldoutData } from '@atlassian/holdout-utils/jira-holdout';
import type { ExperienceAttributes } from '../../common/types.tsx';
import { useExperienceContext } from '../../common/ui/use-experience-context/index.tsx';

type Props = {
	experience: string;
	attributes?: ExperienceAttributes;
};
/**
 * Enhances the tracking of failed experiences by excluding certain client-side errors and
 * allowing for the addition of extra attributes to the failure information. This
 * enables more precise and enriched reporting of issues encountered.
 */
export const useExperienceFail = ({
	experience,
	attributes,
}: Props): ((location: string, error: Error, extraAttributes?: ExperienceAttributes) => void) => {
	const experienceTracker = useExperienceContext();
	const self = useRef({
		name: experience,
		attributes,
		experienceTracker,
	});
	self.current.name = experience;
	self.current.attributes = attributes;
	self.current.experienceTracker = experienceTracker;
	return useCallback(
		(location: string, error: Error, extraAttributes: ExperienceAttributes = {}) => {
			const { current } = self;

			// ignore client side errors from impacting experience metrics

			if ((isLoaderError(error) && error.name === 'ChunkLoadError') || isClientFetchError(error)) {
				return;
			}

			const shardAndRegionAttributes = {
				shard: getMeta('ajs-shard'),
				region: getMeta('ajs-region'),
			};

			const holdoutAttributes: ExperienceAttributes = {
				customHoldout: getIsInJiraHoldoutData().holdout,
			};

			current.experienceTracker.fail({
				name: current.name,
				error,
				attributes: {
					...current.attributes,
					...extraAttributes,
					...shardAndRegionAttributes,
					...holdoutAttributes,
					location,
				},
			});
		},
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		[self],
	);
};
